import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  Play,
  Pause,
  Trash2,
  Download,
  RefreshCw,
  Search,
  Lock,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_ORDER = ['NEW', 'APPROVED', 'BROADCAST', 'REJECTED', 'ARCHIVED'];

const statusColor = (s) => {
  switch (s) {
    case 'NEW':
      return 'bg-amber-100 text-amber-800 border-amber-200';
    case 'APPROVED':
      return 'bg-emerald-100 text-emerald-800 border-emerald-200';
    case 'BROADCAST':
      return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'REJECTED':
      return 'bg-red-100 text-red-800 border-red-200';
    default:
      return 'bg-gray-100 text-gray-700 border-gray-200';
  }
};

const VoiceInbox = () => {
  const [authHeader, setAuthHeader] = useState(
    () => sessionStorage.getItem('kioo_admin_auth') || ''
  );
  const [creds, setCreds] = useState({ user: '', pass: '' });
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [statusFilter, setStatusFilter] = useState('');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [playingId, setPlayingId] = useState(null);
  const audioMapRef = useRef({});

  const authed = useMemo(() => !!authHeader, [authHeader]);

  const login = (e) => {
    e?.preventDefault?.();
    const h = 'Basic ' + btoa(`${creds.user}:${creds.pass}`);
    sessionStorage.setItem('kioo_admin_auth', h);
    setAuthHeader(h);
  };

  const logout = () => {
    sessionStorage.removeItem('kioo_admin_auth');
    setAuthHeader('');
    setItems([]);
  };

  const load = async () => {
    if (!authHeader) return;
    setLoading(true);
    setErr('');
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (q.trim()) params.set('q', q.trim());
      const { data } = await axios.get(
        `${BACKEND_URL}/api/voice-messages/admin?${params}`,
        { headers: { Authorization: authHeader } }
      );
      setItems(data.items || []);
      setCounts(data.counts_by_status || {});
    } catch (e) {
      if (e?.response?.status === 401) {
        setErr('Wrong credentials.');
        logout();
      } else {
        setErr('Could not load submissions.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [authHeader, statusFilter]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(
        `${BACKEND_URL}/api/voice-messages/admin/${id}`,
        { status },
        { headers: { Authorization: authHeader } }
      );
      load();
    } catch (_e) {
      setErr('Update failed.');
    }
  };

  const saveNotes = async (id, admin_notes) => {
    try {
      await axios.patch(
        `${BACKEND_URL}/api/voice-messages/admin/${id}`,
        { admin_notes },
        { headers: { Authorization: authHeader } }
      );
    } catch (_e) {
      setErr('Notes save failed.');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this recording? This cannot be undone.')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/voice-messages/admin/${id}`, {
        headers: { Authorization: authHeader },
      });
      load();
    } catch (_e) {
      setErr('Delete failed.');
    }
  };

  const playToggle = async (id) => {
    let el = audioMapRef.current[id];
    if (!el) {
      // Fetch audio as blob with auth header (Audio src cannot send Authorization)
      try {
        const resp = await axios.get(
          `${BACKEND_URL}/api/voice-messages/admin/${id}/audio`,
          {
            headers: { Authorization: authHeader },
            responseType: 'blob',
          }
        );
        const url = URL.createObjectURL(resp.data);
        el = new Audio(url);
        el.onended = () => setPlayingId(null);
        audioMapRef.current[id] = el;
      } catch (_e) {
        setErr('Playback failed.');
        return;
      }
    }
    if (playingId === id) {
      el.pause();
      setPlayingId(null);
    } else {
      Object.entries(audioMapRef.current).forEach(([k, a]) => {
        if (k !== id) a.pause();
      });
      el.play();
      setPlayingId(id);
    }
  };

  const downloadOne = async (id) => {
    try {
      const resp = await axios.get(
        `${BACKEND_URL}/api/voice-messages/admin/${id}/audio`,
        { headers: { Authorization: authHeader }, responseType: 'blob' }
      );
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `voice-${id.slice(0, 8)}.webm`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (_e) {
      setErr('Download failed.');
    }
  };

  if (!authed) {
    return (
      <div className="px-4 pt-8 pb-6">
        <div className="max-w-sm mx-auto rounded-2xl bg-white border border-gray-100 shadow-sm p-6">
          <div className="w-14 h-14 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
            <Lock className="w-7 h-7 text-emerald-700" />
          </div>
          <h1 className="mt-3 text-xl font-extrabold text-gray-900 text-center">
            Voice Inbox
          </h1>
          <p className="mt-1 text-xs text-center text-gray-500">
            Sign in with your Kioo Radio admin credentials.
          </p>
          <form className="mt-4 space-y-3" onSubmit={login}>
            <input
              data-testid="voice-inbox-user"
              type="text"
              placeholder="Username"
              autoComplete="username"
              value={creds.user}
              onChange={(e) => setCreds({ ...creds, user: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:border-emerald-500 focus:outline-none text-sm"
            />
            <input
              data-testid="voice-inbox-pass"
              type="password"
              placeholder="Password"
              autoComplete="current-password"
              value={creds.pass}
              onChange={(e) => setCreds({ ...creds, pass: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:border-emerald-500 focus:outline-none text-sm"
            />
            <button
              data-testid="voice-inbox-login-btn"
              type="submit"
              className="w-full py-2.5 rounded-full font-semibold text-white bg-emerald-700 hover:bg-emerald-800"
            >
              Sign in
            </button>
          </form>
          {err && (
            <div className="mt-3 text-xs text-red-700 text-center">{err}</div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 pt-4 pb-6" data-testid="voice-inbox-page">
      <div className="flex items-center gap-2 mb-3">
        <h1 className="text-2xl font-extrabold text-gray-900 leading-tight flex-1">
          Voice Inbox
        </h1>
        <button
          data-testid="voice-inbox-refresh"
          onClick={load}
          className="p-2 rounded-full bg-gray-100 hover:bg-gray-200"
          aria-label="Refresh"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
        <button
          data-testid="voice-inbox-logout"
          onClick={logout}
          className="text-xs px-3 py-2 rounded-full bg-gray-100 hover:bg-gray-200 font-semibold"
        >
          Sign out
        </button>
      </div>

      {/* Status filter chips */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        <button
          data-testid="voice-inbox-filter-all"
          onClick={() => setStatusFilter('')}
          className={`px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap ${
            !statusFilter ? 'bg-emerald-700 text-white' : 'bg-gray-100 text-gray-700'
          }`}
        >
          All
        </button>
        {STATUS_ORDER.map((s) => (
          <button
            key={s}
            data-testid={`voice-inbox-filter-${s.toLowerCase()}`}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap ${
              statusFilter === s
                ? 'bg-emerald-700 text-white'
                : 'bg-gray-100 text-gray-700'
            }`}
          >
            {s} <span className="opacity-70">({counts[s] || 0})</span>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative mt-2">
        <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          data-testid="voice-inbox-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && load()}
          placeholder="Search by name, location or recipient…"
          className="w-full pl-9 pr-3 py-2 rounded-full border border-gray-300 focus:border-emerald-500 focus:outline-none text-sm"
        />
      </div>

      {err && (
        <div className="mt-3 text-sm bg-red-50 border border-red-200 text-red-800 rounded-xl p-3">
          {err}
        </div>
      )}

      {/* Items list */}
      <ul className="mt-4 space-y-3" data-testid="voice-inbox-list">
        {items.length === 0 && !loading && (
          <li className="text-center text-sm text-gray-500 py-8">
            No submissions yet.
          </li>
        )}
        {items.map((it) => (
          <li
            key={it.id}
            data-testid={`voice-inbox-item-${it.id}`}
            className="rounded-2xl bg-white border border-gray-100 shadow-sm p-3"
          >
            <div className="flex items-start gap-2">
              <button
                data-testid={`voice-inbox-play-${it.id}`}
                onClick={() => playToggle(it.id)}
                className="w-11 h-11 rounded-full text-white bg-emerald-700 flex items-center justify-center flex-shrink-0"
                aria-label="Play recording"
              >
                {playingId === it.id ? (
                  <Pause className="w-5 h-5" />
                ) : (
                  <Play className="w-5 h-5 fill-current" />
                )}
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-bold text-gray-900 truncate">
                    {it.listener_name}
                  </div>
                  <span
                    className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded border ${statusColor(
                      it.status
                    )}`}
                  >
                    {it.status}
                  </span>
                </div>
                <div className="text-xs text-gray-600 truncate">
                  📍 {it.location} · for: <b>{it.message_for}</b>
                </div>
                <div className="text-[10px] text-gray-400 mt-0.5">
                  {new Date(it.created_at).toLocaleString()} ·{' '}
                  {Math.round(it.duration_seconds || 0)}s ·{' '}
                  {Math.round((it.size_bytes || 0) / 1024)} KB
                  {it.phone && <> · 📞 {it.phone}</>}
                </div>
              </div>
            </div>

            {/* Status buttons */}
            <div className="mt-2 flex gap-1 flex-wrap">
              {STATUS_ORDER.map((s) => (
                <button
                  key={s}
                  data-testid={`voice-inbox-status-${it.id}-${s.toLowerCase()}`}
                  onClick={() => updateStatus(it.id, s)}
                  className={`text-[10px] font-bold px-2 py-1 rounded border ${
                    it.status === s
                      ? statusColor(s)
                      : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  {s}
                </button>
              ))}
              <button
                data-testid={`voice-inbox-download-${it.id}`}
                onClick={() => downloadOne(it.id)}
                className="ml-auto text-[10px] font-bold px-2 py-1 rounded border bg-white text-gray-600 border-gray-200 hover:bg-gray-50 inline-flex items-center gap-1"
              >
                <Download className="w-3 h-3" /> Download
              </button>
              <button
                data-testid={`voice-inbox-delete-${it.id}`}
                onClick={() => remove(it.id)}
                className="text-[10px] font-bold px-2 py-1 rounded border bg-white text-red-600 border-red-200 hover:bg-red-50 inline-flex items-center gap-1"
              >
                <Trash2 className="w-3 h-3" /> Delete
              </button>
            </div>

            {/* Notes */}
            <details className="mt-2 text-xs">
              <summary className="cursor-pointer text-gray-600">
                Internal notes
              </summary>
              <textarea
                data-testid={`voice-inbox-notes-${it.id}`}
                defaultValue={it.admin_notes || ''}
                onBlur={(e) => saveNotes(it.id, e.target.value)}
                rows={2}
                placeholder="Add a note (auto-saves on blur)"
                className="mt-1 w-full text-xs px-2 py-1.5 rounded-lg border border-gray-200 focus:border-emerald-400 focus:outline-none"
              />
            </details>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default VoiceInbox;
